import type { Collab } from "@cabal/shared/domain/session/collab/types.js";
import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import type { SessionProductRead } from "@cabal/shared/domain/session/types.js";
import {
  MAIN_CHAT_PRODUCT_KEY,
  type ProductReadMarkerMap,
  type ProductReadScopeKey,
  type ProductSeenState,
  type ProductUnreadCollabCounts,
  type ProductsSeenMap,
  type UnreadState,
} from "@/types/frontend-types";
import { areDatesEqual, mergeLatestDate, parseDateOrNull } from "./session-date-selectors";

interface ProductWithSeenTimestamp {
  id: string;
  updatedAt: string | null | undefined;
}

interface ProductReadUpdatedAtSelection {
  productReads: ProductReadMarkerMap;
  updatedAtByScope: Record<ProductReadScopeKey, number>;
}

interface MergedProductReadItemsSelection extends ProductReadUpdatedAtSelection {
  didChange: boolean;
}

interface ProductReadMarkerUpdateSelection {
  productReads: ProductReadMarkerMap;
  didChange: boolean;
}

const PRODUCT_SEEN_STATE_FIELDS = [
  "lastChangedProduct",
  "lastAddedCollabs",
  "lastAddedVersion",
] as const satisfies ReadonlyArray<keyof ProductSeenState>;

const EMPTY_PRODUCT_SEEN_STATE: ProductSeenState = {
  lastChangedProduct: null,
  lastAddedCollabs: null,
  lastAddedVersion: null,
};

export function toProductReadScopeKey(input: {
  productId: string | null;
}): ProductReadScopeKey {
  return input.productId ?? MAIN_CHAT_PRODUCT_KEY;
}

export function selectInitialProductsSeen(input: {
  products: readonly ProductWithSeenTimestamp[];
}): ProductsSeenMap {
  return input.products.reduce<ProductsSeenMap>((acc, product) => {
    acc[product.id] = {
      lastChangedProduct: parseDateOrNull(product.updatedAt),
      lastAddedCollabs: null,
      lastAddedVersion: null,
    };
    return acc;
  }, {});
}

export function selectRefreshedProductsSeen(input: {
  products: readonly ProductWithSeenTimestamp[];
  previous: ProductsSeenMap;
}): ProductsSeenMap {
  return input.products.reduce<ProductsSeenMap>((acc, product) => {
    acc[product.id] = {
      lastChangedProduct: parseDateOrNull(product.updatedAt),
      lastAddedCollabs: input.previous[product.id]?.lastAddedCollabs ?? null,
      lastAddedVersion: input.previous[product.id]?.lastAddedVersion ?? null,
    };
    return acc;
  }, {});
}

export function selectUpdatedProductsSeen(input: {
  previous: ProductsSeenMap;
  productId: string;
  patch: Partial<ProductSeenState>;
}): ProductsSeenMap {
  const existing = input.previous[input.productId] ?? EMPTY_PRODUCT_SEEN_STATE;
  const next = selectMergedProductSeenState({
    existing,
    patch: input.patch,
  });

  if (!hasProductSeenStateChanged({ existing, next })) {
    return input.previous;
  }

  return {
    ...input.previous,
    [input.productId]: next,
  };
}

function selectMergedProductSeenState(input: {
  existing: ProductSeenState;
  patch: Partial<ProductSeenState>;
}): ProductSeenState {
  return {
    lastChangedProduct: mergeLatestDate({
      existing: input.existing.lastChangedProduct,
      incoming: input.patch.lastChangedProduct ?? null,
    }),
    lastAddedCollabs: mergeLatestDate({
      existing: input.existing.lastAddedCollabs,
      incoming: input.patch.lastAddedCollabs ?? null,
    }),
    lastAddedVersion: mergeLatestDate({
      existing: input.existing.lastAddedVersion,
      incoming: input.patch.lastAddedVersion ?? null,
    }),
  };
}

function hasProductSeenStateChanged(input: {
  existing: ProductSeenState;
  next: ProductSeenState;
}): boolean {
  return PRODUCT_SEEN_STATE_FIELDS.some((field) =>
    !areDatesEqual({
      first: input.existing[field],
      second: input.next[field],
    }),
  );
}

export function selectProductReadsFromItems(input: {
  items: readonly SessionProductRead[];
  nowTimestamp: number;
}): ProductReadUpdatedAtSelection {
  const productReads: ProductReadMarkerMap = {};
  const updatedAtByScope: Record<ProductReadScopeKey, number> = {};

  input.items.forEach((item) => {
    const scopeKey = toProductReadScopeKey({ productId: item.productId });
    productReads[scopeKey] = {
      productSeenAt: parseDateOrNull(item.productSeenAt),
      collabsReadAt: parseDateOrNull(item.collabsReadAt),
    };
    updatedAtByScope[scopeKey] = selectProductReadUpdatedAt({
      item,
      nowTimestamp: input.nowTimestamp,
    });
  });

  return { productReads, updatedAtByScope };
}

export function selectMergedProductReadItems(input: {
  previous: ProductReadMarkerMap;
  previousUpdatedAtByScope: Record<ProductReadScopeKey, number>;
  items: readonly SessionProductRead[];
  nowTimestamp: number;
}): MergedProductReadItemsSelection {
  let didChange = false;
  const productReads = { ...input.previous };
  const updatedAtByScope = { ...input.previousUpdatedAtByScope };

  input.items.forEach((item) => {
    const scopeKey = toProductReadScopeKey({ productId: item.productId });
    const incomingUpdatedAt = selectProductReadUpdatedAt({
      item,
      nowTimestamp: input.nowTimestamp,
    });
    const knownUpdatedAt =
      updatedAtByScope[scopeKey] ?? Number.NEGATIVE_INFINITY;

    if (incomingUpdatedAt < knownUpdatedAt) {
      return;
    }

    productReads[scopeKey] = {
      productSeenAt: parseDateOrNull(item.productSeenAt),
      collabsReadAt: parseDateOrNull(item.collabsReadAt),
    };
    updatedAtByScope[scopeKey] = incomingUpdatedAt;
    didChange = true;
  });

  return { productReads, updatedAtByScope, didChange };
}

export function selectUpdatedProductReadMarker(input: {
  previous: ProductReadMarkerMap;
  productId: string | null;
  collabsReadAt?: Date | null;
  productSeenAt?: Date | null;
}): ProductReadMarkerUpdateSelection {
  const scopeKey = toProductReadScopeKey({ productId: input.productId });
  const existing = input.previous[scopeKey] ?? {
    collabsReadAt: null,
    productSeenAt: null,
  };

  const next = {
    collabsReadAt: mergeLatestDate({
      existing: existing.collabsReadAt,
      incoming: input.collabsReadAt ?? null,
    }),
    productSeenAt: mergeLatestDate({
      existing: existing.productSeenAt,
      incoming: input.productSeenAt ?? null,
    }),
  };

  const didChange =
    !areDatesEqual({
      first: existing.collabsReadAt,
      second: next.collabsReadAt,
    }) ||
    !areDatesEqual({
      first: existing.productSeenAt,
      second: next.productSeenAt,
    });

  if (!didChange) {
    return { productReads: input.previous, didChange: false };
  }

  return {
    productReads: {
      ...input.previous,
      [scopeKey]: next,
    },
    didChange: true,
  };
}

export function selectIsCollabUnread(input: {
  collab: Collab;
  productId: string | null;
  ownMessageIds: ReadonlySet<string>;
  productReads: ProductReadMarkerMap;
}): boolean {
  if (input.ownMessageIds.has(input.collab.id)) {
    return false;
  }

  const scopeKey = toProductReadScopeKey({ productId: input.productId });
  const readMarker = input.productReads[scopeKey]?.collabsReadAt ?? null;

  if (!readMarker) {
    return true;
  }

  const collabTimestamp = Date.parse(input.collab.createdAt);
  if (Number.isNaN(collabTimestamp)) {
    return false;
  }

  return collabTimestamp > readMarker.getTime();
}

export function selectUnreadCollabCounts(input: {
  chatMessageIds: readonly string[];
  chatMessages: Record<string, Collab>;
  collabs: Record<string, Collab>;
  collabsByProduct: Record<string, string[]>;
  ownMessageIds: ReadonlySet<string>;
  productReads: ProductReadMarkerMap;
}): ProductUnreadCollabCounts {
  const counts: ProductUnreadCollabCounts = {};
  const mainChatMessages = input.chatMessageIds
    .map((messageId) => input.chatMessages[messageId])
    .filter((message): message is Collab => Boolean(message));

  counts[MAIN_CHAT_PRODUCT_KEY] = {
    totalCollabs: mainChatMessages.length,
    unreadCollabs: selectUnreadCount({
      collabs: mainChatMessages,
      productId: null,
      ownMessageIds: input.ownMessageIds,
      productReads: input.productReads,
    }),
  };

  Object.entries(input.collabsByProduct).forEach(([productId, collabIds]) => {
    const productCollabs = collabIds
      .map((collabId) => input.collabs[collabId])
      .filter((collab): collab is Collab => Boolean(collab));

    counts[productId] = {
      totalCollabs: productCollabs.length,
      unreadCollabs: selectUnreadCount({
        collabs: productCollabs,
        productId,
        ownMessageIds: input.ownMessageIds,
        productReads: input.productReads,
      }),
    };
  });

  return counts;
}

export function selectUnreadState(input: {
  products: Record<string, ProductSummary>;
  collabsByProduct: Record<string, string[]>;
  unreadCollabCounts: ProductUnreadCollabCounts;
}): UnreadState {
  const collabUnreadByProduct = Object.entries(input.unreadCollabCounts)
    .filter(([scopeKey]) => scopeKey !== MAIN_CHAT_PRODUCT_KEY)
    .reduce<Record<string, number>>((acc, [scopeKey, item]) => {
      acc[scopeKey] = item.unreadCollabs;
      return acc;
    }, {});

  const unseenSubproductsByProduct = Object.values(input.products).reduce<
    Record<string, number>
  >((acc, product) => {
    acc[product.id] = product.unseenSubproductCount ?? 0;
    return acc;
  }, {});

  return {
    collabs: collabUnreadByProduct,
    versions: {},
    products: unseenSubproductsByProduct,
    collabsByProduct: input.collabsByProduct,
    versionsByProduct: {},
  };
}

export function selectLatestCollabByIds(input: {
  collabIds: readonly string[];
  collabs: Record<string, Collab>;
}): Collab | null {
  return input.collabIds.reduce<Collab | null>((latest, collabId) => {
    const collab = input.collabs[collabId];
    if (!collab) {
      return latest;
    }
    if (!latest) {
      return collab;
    }
    return Date.parse(collab.createdAt) > Date.parse(latest.createdAt)
      ? collab
      : latest;
  }, null);
}

function selectProductReadUpdatedAt(input: {
  item: SessionProductRead;
  nowTimestamp: number;
}): number {
  const parsedUpdatedAt = Date.parse(input.item.updatedAt);
  return Number.isNaN(parsedUpdatedAt) ? input.nowTimestamp : parsedUpdatedAt;
}

function selectUnreadCount(input: {
  collabs: readonly Collab[];
  productId: string | null;
  ownMessageIds: ReadonlySet<string>;
  productReads: ProductReadMarkerMap;
}): number {
  return input.collabs.reduce(
    (acc, collab) =>
      acc +
      (selectIsCollabUnread({
        collab,
        productId: input.productId,
        ownMessageIds: input.ownMessageIds,
        productReads: input.productReads,
      })
        ? 1
        : 0),
    0,
  );
}
